/**
 * What an Approve click on Pending Review should do: go straight through, or ask which
 * changes to approve.
 *
 * Jason, 24 Oct 2026: approving a row for the change you looked at (say its category) must
 * not quietly approve the other pending changes on it (tags, description). One change and
 * nothing missing goes straight through; anything more opens a picker with every option
 * unticked, so nothing is approved that was not chosen.
 *
 * Jason, 31 Oct 2026: the picker is for ONE product with several changes. Rows that each
 * carry a single change go straight through even when the changes differ between rows
 * (6 brand rows + 1 tag row): every change on them was looked at.
 */

/** The pseudo change type for a new item: approving it creates the product. */
export const NEW_ITEM = 'new_item';
/** The pseudo change type for a removal: approving it takes the product out. */
export const REMOVAL = 'removal';

export interface ApprovalRow {
  sku: string;
  suggestedName: string;
  type: 'new_item' | 'field_update' | 'removal';
  changedFields?: string[];
  suggestedDescription?: string | null;
  suggestedImageUrl?: string | null;
  basePrice?: number | null;
  resolvedCategory?: { held: boolean; name: string | null } | null;
}

export interface ApprovalOption {
  key: string;
  label: string;
  /** How many of the rows carry this change. */
  count: number;
}

export interface ApprovalPlan {
  needsChoice: boolean;
  options: ApprovalOption[];
  /** New items going live without these values. */
  missing: Array<{ sku: string; name: string; fields: string[] }>;
}

const LABELS: Record<string, string> = {
  [NEW_ITEM]: 'New item — create it in the app',
  [REMOVAL]: 'Removal — take it out of the app',
  image_url: 'Image',
};

export const changeLabel = (key: string): string =>
  LABELS[key] ?? key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** The changes an approval of this row would apply. */
export function rowChanges(row: ApprovalRow): string[] {
  if (row.type === 'removal') return [REMOVAL];
  if (row.type !== 'field_update') return [NEW_ITEM];
  return Array.from(new Set(row.changedFields ?? []));
}

/** Values a new item would go live without. Existing products already have theirs. */
export function missingFields(row: ApprovalRow): string[] {
  if (row.type !== 'new_item') return [];
  const missing: string[] = [];
  if (!row.suggestedDescription?.trim()) missing.push('description');
  if (!row.suggestedImageUrl?.trim()) missing.push('image');
  if (!(Number(row.basePrice) > 0)) missing.push('price');
  if (row.resolvedCategory?.held) missing.push('category (unmapped)');
  return missing;
}

export function planApproval(rows: ApprovalRow[]): ApprovalPlan {
  const counts = new Map<string, number>();
  let rowWithSeveral = false;
  for (const row of rows) {
    const changes = rowChanges(row);
    if (changes.length > 1) rowWithSeveral = true;
    for (const c of changes) counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  const options = Array.from(counts).map(([key, count]) => ({ key, label: changeLabel(key), count }));
  // New items first, then the field changes in a stable order.
  options.sort((a, b) => (a.key === NEW_ITEM ? -1 : b.key === NEW_ITEM ? 1 : a.label.localeCompare(b.label)));

  const missing = rows
    .map((r) => ({ sku: r.sku, name: r.suggestedName, fields: missingFields(r) }))
    .filter((m) => m.fields.length > 0);

  return {
    // A removal takes a live product out, so mixed in with other changes it is always asked.
    needsChoice: rowWithSeveral || missing.length > 0 || (counts.has(REMOVAL) && options.length > 1),
    options,
    missing,
  };
}
